/**
 * The board configuration contract, mirrored from the server (RVW2 step 6).
 *
 * ⚠ ONE TABLE, TWO READERS. `backend/config/board_config_defaults.py` owns it;
 * this file is its mirror so the panel can render synchronously. The server
 * serves the same table at `GET /calcul/board-schema`, and
 * `boardConfigSchema.test.js` fails when the two drift apart.
 *
 * There is NO advanced tier: every declared option is visible. `dependsOn`
 * marks a field whose choices narrow with another one — a unit list follows
 * its dimension — which is not the same thing as hiding it.
 */
import { LIST_SEPARATOR } from './calculConstants';

export const FIELD = Object.freeze({
  ENUM: 'enum', INT: 'int', BOOL: 'bool', MULTI: 'multi',
});

const enumField = (choices, def, key, extra = {}) => Object.freeze({
  kind: FIELD.ENUM, choices: Object.freeze(choices), default: def, key, ...extra,
});
const intField = (min, max, def, key) => Object.freeze({
  kind: FIELD.INT, min, max, default: def, key,
});
const boolField = (def, key) => Object.freeze({ kind: FIELD.BOOL, default: def, key });
const multiField = (choices, def, key) => Object.freeze({
  kind: FIELD.MULTI, choices: Object.freeze(choices), default: Object.freeze(def), key,
});

/** Unit factors are the server's business; the panel only needs the lists. */
export const DIMENSIONS = Object.freeze({
  longueur: ['km', 'hm', 'dam', 'm', 'dm', 'cm', 'mm'],
  masse: ['t', 'kg', 'hg', 'dag', 'g', 'dg', 'cg', 'mg'],
  capacite: ['hL', 'daL', 'L', 'dL', 'cL', 'mL'],
  aire: ['km²', 'ha', 'a', 'm²', 'dm²', 'cm²', 'mm²'],
  volume: ['m³', 'dm³', 'cm³', 'mm³', 'hL', 'daL', 'L', 'dL', 'cL', 'mL'],
  duree: ['j', 'h', 'min', 's'],
  monnaie: ['€', 'centime'],
  vitesse: ['km/h', 'm/s'],
});

export const ALL_UNITS = Object.freeze(
  [...new Set(Object.values(DIMENSIONS).flat())]);

export const SHAPE_LABELS = Object.freeze({
  carre: 'Carré', rectangle: 'Rectangle', triangle: 'Triangle',
  parallelogramme: 'Parallélogramme', cube: 'Cube', pave: 'Pavé droit',
});

export const BOARD_SCHEMA = Object.freeze({
  conversions: Object.freeze({
    dimension: enumField(Object.keys(DIMENSIONS), 'longueur', 'dimension'),
    from_unit: enumField(ALL_UNITS, 'm', 'depuis', { dependsOn: 'dimension' }),
    to_unit: enumField(ALL_UNITS, 'cm', 'vers', { dependsOn: 'dimension' }),
  }),
  perimetre: Object.freeze({
    shapes: multiField(['carre', 'rectangle', 'triangle'], ['carre', 'rectangle'], 'formes'),
    decimals: intField(0, 2, 0, 'decimales'),
  }),
  aires: Object.freeze({
    shapes: multiField(['carre', 'rectangle', 'triangle', 'parallelogramme'],
      ['carre', 'rectangle'], 'formes'),
    decimals: intField(0, 2, 0, 'decimales'),
  }),
  volumes: Object.freeze({
    shapes: multiField(['cube', 'pave'], ['cube'], 'formes'),
    decimals: intField(0, 2, 0, 'decimales'),
  }),
  fractions: Object.freeze({
    operation: enumField(['addition', 'soustraction', 'multiplication', 'division'],
      'addition', 'operation'),
    sign: enumField(['positifs', 'mixtes', 'operande_negatif', 'resultat_negatif'],
      'positifs', 'signe'),
    denominator_max: intField(2, 20, 10, 'denominateur'),
  }),
  priorites: Object.freeze({
    operands: intField(2, 4, 3, 'operandes'),
    depth: intField(0, 2, 0, 'parentheses'),
    number_set: enumField(['naturels', 'relatifs', 'rationnels'], 'naturels', 'ensemble'),
  }),
  'puissances-racines': Object.freeze({
    exponent_min: intField(2, 5, 2, 'exposant_min'),
    exponent_max: intField(2, 5, 3, 'exposant_max'),
    perfect_squares_only: boolField(false, 'carres_parfaits'),
  }),
  'nombres-en-lettres': Object.freeze({
    mode: enumField(['chiffres-lettres', 'lettres-chiffres'], 'chiffres-lettres', 'mode'),
    nombres: enumField(['entiers', 'decimaux'], 'entiers', 'nombres'),
    min: intField(0, 999999999, 0, 'min'),
    max: intField(0, 999999999, 1000, 'max'),
  }),
  english: Object.freeze({
    skill: enumField(['mixte', 'english_spelling', 'english_grammar'], 'mixte', 'competence'),
    level: enumField(['mixte', 'beginner', 'intermediate', 'advanced'], 'mixte', 'niveau'),
  }),
  code: Object.freeze({
    language: enumField(['scratch', 'python'], 'scratch', 'langage'),
    door: enumField(['workshop', 'questions'], 'workshop', 'porte'),
    kind: enumField(['programme_de_calcul', 'dessins', 'autre'], 'programme_de_calcul', 'genre'),
    level: intField(1, 5, 1, 'niveau'),
  }),
});

export const CONFIGURABLE_BOARDS = Object.freeze(Object.keys(BOARD_SCHEMA));

export const boardSchema = (board) => BOARD_SCHEMA[board] || null;

export const boardDefaults = (board) => Object.entries(boardSchema(board) || {})
  .reduce((values, [name, field]) => ({
    ...values,
    [name]: field.kind === FIELD.MULTI ? [...field.default] : field.default,
  }), {});

/** The choices a field offers right now — narrowed by what it depends on. */
export function fieldChoices(board, name, values = {}) {
  const field = (boardSchema(board) || {})[name];
  if (!field || !field.choices) return [];
  if (field.dependsOn === 'dimension') {
    return DIMENSIONS[values.dimension || boardDefaults(board).dimension] || [];
  }
  return [...field.choices];
}

/** A unit from another quantity would be an impossible pair: both re-seat inside the new one. */
export function seatUnits(board, dimension) {
  if (board !== 'conversions') return {};
  const units = fieldChoices(board, 'from_unit', { dimension });
  return { from_unit: units[0], to_unit: units[units.length - 1] };
}

/** Integer fields are chips too: the panel has one widget, not a second one. */
export const intChoices = (field) => Array.from(
  { length: field.max - field.min + 1 }, (_, index) => field.min + index);

/**
 * The board's options as query parameters — the exact keys the server reads.
 * A value equal to its default is still sent: a shared link must reproduce the
 * configuration even when the defaults later change.
 */
export function boardQuery(board, values = {}) {
  const fields = boardSchema(board);
  if (!fields) return '';
  const query = new URLSearchParams();
  const settings = { ...boardDefaults(board), ...values };
  Object.entries(fields).forEach(([name, field]) => {
    const value = settings[name];
    if (value === undefined || value === null) return;
    query.set(field.key, field.kind === FIELD.MULTI ? value.join(',') : String(value));
  });
  return query.toString();
}

const TRUE_WORDS = Object.freeze(['1', 'true', 'vrai', 'oui']);

/** One raw query value, typed by its field; `undefined` when absent or invalid. */
function readField(board, name, field, raw, values) {
  if (raw === null || raw === '') return undefined;
  if (field.kind === FIELD.INT) {
    const value = Number(raw);
    return Number.isInteger(value) && value >= field.min && value <= field.max ? value : undefined;
  }
  if (field.kind === FIELD.BOOL) return TRUE_WORDS.includes(String(raw).toLowerCase());
  if (field.kind === FIELD.MULTI) {
    const picked = [...new Set(String(raw).split(LIST_SEPARATOR))]
      .filter((value) => field.choices.includes(value));
    return picked.length ? picked : undefined;
  }
  return fieldChoices(board, name, values).includes(raw) ? raw : undefined;
}

/**
 * The board's options read back from a link — the mirror of the server's
 * `board_config_service.from_query`. A missing or invalid value falls back to
 * its default (a unit outside its quantity re-seats inside it), so a cold link
 * always yields a complete option set.
 */
export function parseBoardQuery(board, params) {
  const fields = boardSchema(board);
  if (!fields) return null;
  const defaults = boardDefaults(board);
  return Object.entries(fields).reduce((values, [name, field]) => {
    const read = readField(board, name, field, params.get(field.key), values);
    if (read !== undefined) return { ...values, [name]: read };
    const fits = !field.dependsOn || fieldChoices(board, name, values).includes(defaults[name]);
    return { ...values, [name]: fits ? defaults[name] : seatUnits(board, values.dimension)[name] };
  }, {});
}

/** What the session request carries: the validated-by-the-server option set. */
export function boardPayload(board, values = {}) {
  return boardSchema(board) ? { ...boardDefaults(board), ...values } : null;
}

/** URL spelling of a unit, matching the server's own public pages. */
const UNIT_SLUGS = Object.freeze({
  'm²': 'm2', 'dm²': 'dm2', 'cm²': 'cm2', 'mm²': 'mm2', 'km²': 'km2',
  'm³': 'm3', 'dm³': 'dm3', 'cm³': 'cm3', 'mm³': 'mm3',
  'km/h': 'km-h', 'm/s': 'm-s', '€': 'euro',
});
export const unitSlug = (unit) => UNIT_SLUGS[unit] || String(unit).toLowerCase();
export const conversionPath = (from, to) => `/calcul/conversions/${unitSlug(from)}-${unitSlug(to)}`;
